/**
 * Axios instance configured for Orval-generated API hooks.
 * Handles authentication, token refresh, and request/response interceptors.
 * Supports acting-as AI agent users via X-Acting-As-AI-Agent header.
 * Detects network errors for backend availability status.
 *
 * Token Refresh Strategy:
 * - Proactive: Before each request, check if token expires within 60s and refresh preemptively
 * - Reactive: If a 401 still occurs (clock skew, server-side revocation), refresh and retry
 * - Single refresh at a time: All concurrent requests queue and wait for one refresh
 */
import axios, { AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import { API_BASE_URL, API_ENDPOINTS, isCapacitor } from './config';
import { getActingAsHeaderValue } from '../contexts/ActingAsContext';
import { getActiveCompanyHeaderValue } from '../contexts/ActiveCompanyContext';
import { isBackendUnavailableError } from '../contexts/ApiStatusContext';
import { clearEmulationData } from '../contexts/EmulationContext';
import { clearDemoMode } from '../auth/demoSession';
// Leaf module — importing the auth session keys from here (rather than from
// AuthContext, which imports THIS file) keeps the graph acyclic.
import { AUTH_STORAGE_KEYS } from '../auth/storageKeys';

const STORAGE_KEYS = {
  ...AUTH_STORAGE_KEYS,
  // External portal users use different token keys
  externalAccessToken: 'external_access_token',
  externalRefreshToken: 'external_refresh_token',
} as const;

// Proactive refresh threshold - refresh if token expires within this many seconds
const TOKEN_REFRESH_THRESHOLD_SECONDS = 60;

// Custom events
export const SESSION_EXPIRED_EVENT = 'panluma:session-expired';
export const VERSION_MISMATCH_EVENT = 'panluma:version-mismatch';
export const BACKEND_UNAVAILABLE_EVENT = 'panluma:backend-unavailable';
export const REQUEST_SUCCESS_EVENT = 'panluma:request-success';
export const API_HTML_RESPONSE_EVENT = 'panluma:api-html-response';

export const emitApiHtmlResponse = () => {
  window.dispatchEvent(new CustomEvent(API_HTML_RESPONSE_EVENT));
};

export const emitSessionExpired = () => {
  window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
};

export const resetSessionExpiredGuard = () => {
  sessionExpiredEmitted = false;
};

export const emitBackendUnavailable = () => {
  window.dispatchEvent(new CustomEvent(BACKEND_UNAVAILABLE_EVENT));
};

export const emitRequestSuccess = () => {
  window.dispatchEvent(new CustomEvent(REQUEST_SUCCESS_EVENT));
};

// Frontend build version (injected at build time via env var)
const FRONTEND_BUILD_VERSION = import.meta.env.VITE_BUILD_VERSION || 'local';
let versionMismatchDetected = false;
let versionLogged = false;

// Log frontend version on startup for debugging
console.log('[Version] Frontend build:', FRONTEND_BUILD_VERSION);

// Create axios instance
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'X-PanLuma-Client': isCapacitor ? 'mobile' : 'web',
  },
});

// Single refresh promise - all requests wait on this if refresh is in progress
let refreshPromise: Promise<boolean> | null = null;

/**
 * Decode JWT payload without verification (just base64 decode)
 */
function decodeJwtPayload(token: string): { exp?: number } | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = parts[1];
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

/**
 * Check if access token is expired or about to expire
 */
function isTokenExpiringSoon(token: string): boolean {
  const payload = decodeJwtPayload(token);
  if (!payload?.exp) return true;
  const nowSeconds = Math.floor(Date.now() / 1000);
  return payload.exp <= nowSeconds + TOKEN_REFRESH_THRESHOLD_SECONDS;
}

/**
 * Perform token refresh. Returns true if successful, false otherwise.
 * Only one refresh runs at a time - concurrent calls share the same promise.
 */
async function refreshTokens(): Promise<boolean> {
  // If refresh already in progress, wait for it
  if (refreshPromise) {
    return refreshPromise;
  }

  const refreshToken = localStorage.getItem(STORAGE_KEYS.refreshToken);
  if (!refreshToken) {
    handleSessionExpired();
    return false;
  }

  // Create the refresh promise
  refreshPromise = (async () => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}${API_ENDPOINTS.auth.refresh}`,
        {},
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${refreshToken}`,
          },
        }
      );

      const tokens = response.data.data;
      localStorage.setItem(STORAGE_KEYS.accessToken, tokens.access_token);
      localStorage.setItem(STORAGE_KEYS.refreshToken, tokens.refresh_token);
      sessionExpiredEmitted = false;
      return true;
    } catch {
      handleSessionExpired();
      return false;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

/**
 * Handle session expiration - clear tokens and notify app.
 * Guarded so concurrent 401s only trigger one session-expired event.
 */
let sessionExpiredEmitted = false;
function handleSessionExpired() {
  localStorage.removeItem(STORAGE_KEYS.accessToken);
  localStorage.removeItem(STORAGE_KEYS.refreshToken);
  localStorage.removeItem(STORAGE_KEYS.user);
  clearEmulationData();
  clearDemoMode();
  if (!sessionExpiredEmitted) {
    sessionExpiredEmitted = true;
    emitSessionExpired();
  }
}

// Request interceptor - proactive token refresh before requests
axiosInstance.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // Check for main app token first, then fall back to external portal token
    let token = localStorage.getItem(STORAGE_KEYS.accessToken);
    const isExternalToken = !token;
    if (!token) {
      token = localStorage.getItem(STORAGE_KEYS.externalAccessToken);
    }

    // Proactive refresh if token is expiring soon (only for main app tokens)
    if (token && !isExternalToken && isTokenExpiringSoon(token)) {
      const success = await refreshTokens();
      if (!success) {
        return Promise.reject(new Error('Session expired'));
      }
      token = localStorage.getItem(STORAGE_KEYS.accessToken);
    }

    // Add auth header
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add acting-as header if in acting-as mode (only for internal users)
    if (!isExternalToken) {
      const actingAsUserId = getActingAsHeaderValue();
      if (actingAsUserId && config.headers) {
        config.headers['X-Acting-As-AI-Agent'] = actingAsUserId;
      }
      // Multi-company P2: active company filter (UI list-filter hint)
      const activeCompanyId = getActiveCompanyHeaderValue();
      if (activeCompanyId && config.headers) {
        config.headers['X-Active-Company-Id'] = activeCompanyId;
      }
    }

    // Let axios auto-set Content-Type for FormData (multipart/form-data with boundary)
    if (config.data instanceof FormData && config.headers) {
      delete config.headers['Content-Type'];
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - reactive 401 handling as fallback
axiosInstance.interceptors.response.use(
  (response) => {
    // Detect HTML responses that should be JSON - this happens when CDN/proxy
    // intercepts errors and returns the SPA HTML page instead of the API response
    const contentType = response.headers['content-type'] || '';
    const isHtmlResponse = contentType.includes('text/html');
    const isApiRequest = response.config.url?.includes('/api/');

    // Skip this check when the caller explicitly requested a non-JSON response type
    const expectsJson = !response.config.responseType || response.config.responseType === 'json';

    if (isHtmlResponse && isApiRequest && expectsJson) {
      console.error('[API Error] Received HTML instead of JSON - likely a proxy/CDN configuration issue', {
        url: response.config.url,
        method: response.config.method?.toUpperCase(),
        status: response.status,
        contentType,
      });
      emitApiHtmlResponse();
      // Throw an error so the mutation/query fails properly
      const error = new Error('Received HTML response instead of JSON. This may indicate a server configuration issue.');
      (error as Error & { isHtmlResponse: boolean }).isHtmlResponse = true;
      throw error;
    }

    emitRequestSuccess();

    // Check for version mismatch - only prompt refresh when frontend is OLDER than backend.
    // During deployment, frontend deploys first (S3 is fast) while backend takes longer (ECS rolling update).
    // A newer frontend talking to an older backend is expected and should NOT trigger refresh.
    // We only want to refresh when the backend has been updated and the frontend is stale.
    const backendVersion = response.headers['x-api-version'];
    if (!versionLogged) {
      versionLogged = true;
      // Log version check on first API response for debugging
      console.log('[Version] Backend:', backendVersion || 'not exposed', '| Frontend:', FRONTEND_BUILD_VERSION);
    }

    // Check if we need version mismatch handling
    const isProductionVersions = backendVersion && backendVersion !== 'local' && FRONTEND_BUILD_VERSION !== 'local';

    if (isProductionVersions && backendVersion === FRONTEND_BUILD_VERSION) {
      // Versions match - clear any stored initial version (deployment complete or successful refresh)
      sessionStorage.removeItem('initialBackendVersion');
    } else if (isProductionVersions && backendVersion !== FRONTEND_BUILD_VERSION && !versionMismatchDetected) {
      // Store the backend version we first saw - only trigger refresh if backend changes again
      // This handles the case where frontend is newer (just deployed) and backend is older (still deploying)
      const storedBackendVersion = sessionStorage.getItem('initialBackendVersion');
      if (!storedBackendVersion) {
        // First time seeing a mismatch - store the backend version and don't refresh yet
        // The frontend is likely newer than the backend (just deployed)
        sessionStorage.setItem('initialBackendVersion', backendVersion);
        console.log('[Version] Mismatch detected but frontend may be newer. Storing initial backend version:', backendVersion);
      } else if (storedBackendVersion !== backendVersion) {
        // Backend version changed from what we initially saw - this means backend was updated
        // and our frontend is now stale. Time to refresh.
        versionMismatchDetected = true;
        console.log('[Version] Backend updated! Was:', storedBackendVersion, 'Now:', backendVersion, '| Frontend:', FRONTEND_BUILD_VERSION);
        sessionStorage.removeItem('initialBackendVersion');
        window.dispatchEvent(
          new CustomEvent(VERSION_MISMATCH_EVENT, {
            detail: { frontendVersion: FRONTEND_BUILD_VERSION, backendVersion },
          })
        );
      }
      // If storedBackendVersion === backendVersion, we already know about this mismatch
      // and are waiting for backend to update (or user to manually refresh)
    }
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Handle 401 - try refresh once as fallback (e.g., server revoked token)
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const success = await refreshTokens();
      if (success) {
        // Retry with new token
        const newToken = localStorage.getItem(STORAGE_KEYS.accessToken);
        if (newToken && originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
        }
        return axiosInstance(originalRequest);
      }
      // Refresh failed - error already handled in refreshTokens()
      return Promise.reject(error);
    }

    // Handle 503 (backend scaled to zero)
    if (isBackendUnavailableError(error)) {
      emitBackendUnavailable();
    }

    // Log other errors for debugging
    if (error.response?.status && error.response.status >= 400 && error.response.status !== 401) {
      const responseData = error.response.data as {
        detail?: { code?: string; message?: string; error_id?: string };
      } | undefined;
      const detail = responseData?.detail;
      console.warn('[API Error]', {
        status: error.response.status,
        code: detail?.code || 'UNKNOWN',
        message: detail?.message || error.message,
        errorId: detail?.error_id,
        path: originalRequest.url,
        method: originalRequest.method?.toUpperCase(),
      });
    }

    return Promise.reject(error);
  }
);

/**
 * Get a valid access token, refreshing proactively if it's expiring soon.
 * Use this for non-axios requests (e.g., streaming fetch/SSE) that bypass interceptors.
 */
export async function getValidAccessToken(): Promise<string> {
  let token = localStorage.getItem(STORAGE_KEYS.accessToken);
  if (!token) {
    const external = localStorage.getItem(STORAGE_KEYS.externalAccessToken);
    if (external) return external;
    handleSessionExpired();
    throw new Error('No access token available');
  }
  if (isTokenExpiringSoon(token)) {
    const success = await refreshTokens();
    if (!success) {
      throw new Error('Session expired');
    }
    token = localStorage.getItem(STORAGE_KEYS.accessToken);
    if (!token) {
      throw new Error('No access token after refresh');
    }
  }
  return token;
}

/**
 * Custom instance function for Orval to use
 */
export const customInstance = async <T>(config: AxiosRequestConfig): Promise<T> => {
  const response = await axiosInstance.request<T>(config);
  return response.data;
};

/**
 * Helper functions for API calls
 */
export async function apiGet<T>(endpoint: string): Promise<T> {
  const response = await axiosInstance.get<T>(endpoint);
  return response.data;
}

export async function apiPost<T>(endpoint: string, data?: unknown): Promise<T> {
  const response = await axiosInstance.post<T>(endpoint, data);
  return response.data;
}

export async function apiPut<T>(endpoint: string, data?: unknown): Promise<T> {
  const response = await axiosInstance.put<T>(endpoint, data);
  return response.data;
}

export async function apiDelete(endpoint: string): Promise<void> {
  await axiosInstance.delete(endpoint);
}

export async function apiPatch<T>(endpoint: string, data?: unknown): Promise<T> {
  const response = await axiosInstance.patch<T>(endpoint, data);
  return response.data;
}

export async function apiRequest<T>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
  endpoint: string,
  data?: unknown
): Promise<T> {
  const response = await axiosInstance.request<T>({
    method,
    url: endpoint,
    data,
  });
  return response.data;
}

export default axiosInstance;
